import { useMemo, useState } from 'react'
import { MedicationAdministration } from '../fhir'
import Client from 'fhirclient/lib/Client'
import { Actions, useAdministrationStateContext } from '../components/state'
import { toDatePickerMoment } from '../utilities'

interface UseLoadAdministrationsParams {
    setLoadingAdministrations: (loading: boolean) => void
    client: Client
    forceLatestAdministrationTime: boolean
    latestAdministrationTime: moment.Moment | string
}

export const useLoadAdministrations = ({
    setLoadingAdministrations,
    client,
    forceLatestAdministrationTime,
    latestAdministrationTime }: UseLoadAdministrationsParams) => {
    const [selectedAdministrationId, setSelectedAdministrationId] = useState<number | undefined>(undefined)
    const { dispatch } = useAdministrationStateContext()

    const loadAdministrations = useMemo(() => {
        return async () => {
            setLoadingAdministrations(true)
            const administrationResults = await client
                .request(`/MedicationAdministration?patient=${client.patient.id}`, {
                    resolveReferences: 'medicationReference',
                    pageLimit: 0,
                    flat: true
                })
                .then(data => {
                    data = data.sort((a: MedicationAdministration, b: MedicationAdministration) => {
                        return toDatePickerMoment(b.effectiveDateTime).diff(toDatePickerMoment(a.effectiveDateTime))
                    })

                    for (let index = 0; index < data.length; index++) {
                        data[index].listId = index
                    }

                    return data
                })
                .catch((error) => {
                    dispatch(Actions.AddError({ message: 'Error occured while loading patient administrations.', stacktrace: error.message }))
                })

            setLoadingAdministrations(false)

            return administrationResults
        }
    }, [client, dispatch, setLoadingAdministrations])

    const splitAdministrations = useMemo(() => {
        return (administrationResults: MedicationAdministration[]) => {
            if(administrationResults) {
                const selectedAdministration = administrationResults.find(x => x.listId === selectedAdministrationId);
                const selectedEffectiveDateTime = selectedAdministration?.effectiveDateTime;
                if (administrationResults) {
                    if (forceLatestAdministrationTime && selectedAdministrationId !== undefined
                        && selectedAdministration && selectedEffectiveDateTime) {
                        const { scheduled, prior } = administrationResults.reduce((
                            acc: {
                                scheduled: MedicationAdministration[],
                                prior: MedicationAdministration[]
                            }, curr) => {
                            if (curr.listId !== selectedAdministrationId) {
                                if (curr.effectiveDateTime >= selectedEffectiveDateTime) {
                                    acc.scheduled.push(curr);
                                } else {
                                    acc.prior.push(curr);
                                }
                            }
                            return acc;
                        }, { scheduled: [], prior: [] });

                        const updatedPrior = [selectedAdministration, ...prior];
                        return { scheduledAdministrations: scheduled, priorAdministrations: updatedPrior }
                    } else {
                        return { scheduledAdministrations: [], priorAdministrations: administrationResults }
                    }
                }
            }

            return { scheduledAdministrations: [], priorAdministrations: [] }
        }
    }, [forceLatestAdministrationTime, selectedAdministrationId])

    return useMemo(() => {
        return {
            loadAdministrations,
            selectedAdministrationId,
            setSelectedAdministrationId,
            splitAdministrations
        }
    },
        [
            loadAdministrations,
            selectedAdministrationId,
            setSelectedAdministrationId,
            splitAdministrations
        ])
}
